import { Pencil, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { LiksButton } from '../../components/liks/LiksButton/LiksButton';
import { LiksConfirmDialog } from '../../components/liks/LiksConfirmDialog/LiksConfirmDialog';
import { LiksIconButton } from '../../components/liks/LiksIconButton/LiksIconButton';
import type { ChatbotTrainingExampleWithIdentity } from '../../types/chatbot';

function formatSortOrderForInput(value: number | null | undefined): string {
  return value == null ? '' : String(value);
}

function parseSortOrderInput(value: string): number | null | undefined {
  const trimmed = value.trim();
  if (trimmed === '') {
    return null;
  }

  const parsed = Number(trimmed);
  if (!Number.isInteger(parsed)) {
    return undefined;
  }

  return parsed;
}

function normalizeExampleSortOrder(
  value: number | null | undefined,
): number | null {
  return value == null ? null : value;
}

type ChatbotTrainingExampleCardProps = {
  example: ChatbotTrainingExampleWithIdentity;
  questionLabel: string;
  answerLabel: string;
  sortOrderLabel: string;
  sortOrderPlaceholder: string;
  sortOrderHint: string;
  editLabel: string;
  deleteLabel: string;
  deleteTitle: string;
  deleteMessage: string;
  deleteConfirmLabel: string;
  saveLabel: string;
  cancelLabel: string;
  canManage: boolean;
  isSaving: boolean;
  isDeleting: boolean;
  onSave: (
    input: { question: string; answer: string; sortOrder: number | null },
    options: { onSuccess: () => void },
  ) => void;
  onDelete: () => void;
};

export function ChatbotTrainingExampleCard({
  example,
  questionLabel,
  answerLabel,
  sortOrderLabel,
  sortOrderPlaceholder,
  sortOrderHint,
  editLabel,
  deleteLabel,
  deleteTitle,
  deleteMessage,
  deleteConfirmLabel,
  saveLabel,
  cancelLabel,
  canManage,
  isSaving,
  isDeleting,
  onSave,
  onDelete,
}: ChatbotTrainingExampleCardProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [question, setQuestion] = useState(example.question);
  const [answer, setAnswer] = useState(example.answer);
  const [sortOrderInput, setSortOrderInput] = useState(() =>
    formatSortOrderForInput(example.sortOrder),
  );

  const parsedSortOrder = parseSortOrderInput(sortOrderInput);
  const isSortOrderInvalid =
    sortOrderInput.trim() !== '' && parsedSortOrder === undefined;
  const nextSortOrder = isSortOrderInvalid ? null : (parsedSortOrder ?? null);
  const currentSortOrder = normalizeExampleSortOrder(example.sortOrder);

  const isDirty =
    question.trim() !== example.question.trim() ||
    answer.trim() !== example.answer.trim() ||
    nextSortOrder !== currentSortOrder;
  const canSave =
    !isSaving &&
    isDirty &&
    question.trim() !== '' &&
    answer.trim() !== '' &&
    !isSortOrderInvalid;

  const resetForm = () => {
    setQuestion(example.question);
    setAnswer(example.answer);
    setSortOrderInput(formatSortOrderForInput(example.sortOrder));
  };

  const handleStartEdit = () => {
    resetForm();
    setIsEditing(true);
  };

  const handleCancel = () => {
    resetForm();
    setIsEditing(false);
  };

  const handleSave = () => {
    if (!canSave || parsedSortOrder === undefined) {
      return;
    }

    onSave(
      {
        question: question.trim(),
        answer: answer.trim(),
        sortOrder: parsedSortOrder,
      },
      { onSuccess: () => setIsEditing(false) },
    );
  };

  return (
    <>
      <article
        className={[
          'chatbot-training__card',
          !isEditing && 'chatbot-training__card--display',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {isEditing ? (
          <>
            <div className="chatbot-training__card-header">
              <h3 className="chatbot-training__card-title">{questionLabel}</h3>
            </div>
            <textarea
              className="chatbot-training__textarea"
              value={question}
              onChange={(event) => setQuestion(event.target.value)}
              rows={3}
              disabled={isSaving}
              aria-label={questionLabel}
            />
            <span className="chatbot-training__field-label">{answerLabel}</span>
            <textarea
              className="chatbot-training__textarea"
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              rows={5}
              disabled={isSaving}
              aria-label={answerLabel}
            />
            <span className="chatbot-training__field-label">
              {sortOrderLabel}
            </span>
            <input
              className="chatbot-training__input"
              type="number"
              inputMode="numeric"
              step={1}
              value={sortOrderInput}
              onChange={(event) => setSortOrderInput(event.target.value)}
              placeholder={sortOrderPlaceholder}
              disabled={isSaving}
              aria-label={sortOrderLabel}
              aria-invalid={isSortOrderInvalid}
              aria-describedby={`${example.id}-sort-order-hint`}
            />
            <p
              id={`${example.id}-sort-order-hint`}
              className="chatbot-training__field-hint"
            >
              {sortOrderHint}
            </p>
            <div className="chatbot-training__card-actions">
              <LiksButton
                type="button"
                variant="secondary"
                onClick={handleCancel}
                disabled={isSaving}
              >
                {cancelLabel}
              </LiksButton>
              <LiksButton
                type="button"
                onClick={handleSave}
                disabled={!canSave}
              >
                {saveLabel}
              </LiksButton>
            </div>
          </>
        ) : (
          <div className="chatbot-training__card-fields">
            <div className="chatbot-training__display-top">
              <div className="chatbot-training__field chatbot-training__field--question">
                <span className="chatbot-training__field-label">
                  {questionLabel}
                </span>
                <p className="chatbot-training__value">{example.question}</p>
              </div>
              {canManage ? (
                <div className="chatbot-training__card-header-actions">
                  <LiksIconButton
                    type="button"
                    variant="on-light"
                    className="chatbot-training__edit-button"
                    aria-label={editLabel}
                    onClick={handleStartEdit}
                  >
                    <Pencil strokeWidth={2} aria-hidden />
                  </LiksIconButton>
                  <LiksIconButton
                    type="button"
                    variant="on-light"
                    className="chatbot-training__delete-button"
                    aria-label={deleteLabel}
                    disabled={isDeleting}
                    onClick={() => setIsDeleteDialogOpen(true)}
                  >
                    <Trash2 strokeWidth={2} aria-hidden />
                  </LiksIconButton>
                </div>
              ) : null}
            </div>
            <div className="chatbot-training__field">
              <span className="chatbot-training__field-label">
                {answerLabel}
              </span>
              <p className="chatbot-training__value">{example.answer}</p>
            </div>
            {example.sortOrder != null ? (
              <div className="chatbot-training__field chatbot-training__field--compact">
                <span className="chatbot-training__field-label">
                  {sortOrderLabel}
                </span>
                <p className="chatbot-training__value chatbot-training__value--compact">
                  {example.sortOrder}
                </p>
              </div>
            ) : null}
          </div>
        )}
      </article>

      <LiksConfirmDialog
        isOpen={isDeleteDialogOpen}
        title={deleteTitle}
        message={deleteMessage}
        confirmLabel={deleteConfirmLabel}
        cancelLabel={cancelLabel}
        onConfirm={() => {
          setIsDeleteDialogOpen(false);
          onDelete();
        }}
        onCancel={() => {
          if (!isDeleting) {
            setIsDeleteDialogOpen(false);
          }
        }}
      />
    </>
  );
}
